import { IMessageTemplateItem } from "us.collection.case/interfaces/MessageTemplate/IMessageTemplate";
import { IFilter } from "us.collection.case/components/MessageTemplates/Interface";
import _ from "lodash";
import { appInsights } from "us.helper";
import { DocumentType, ReceiverType } from "us.collection.case/constants";
import { LevelType, EntityType } from "us.collection/constants";

/**
 * filter by document type and name
 * @param data array of message template objects
 * @param filter filter values
 * @returns fillered data array
 */
export const filterMessageTemplates = (
  data: Array<IMessageTemplateItem>,
  filter: IFilter
): Array<IMessageTemplateItem> => {
  const { type, name } = filter;
  try {
    if (type === "all") {
      return _.orderBy(
        data.filter(({ templateName }: IMessageTemplateItem) => {
          return templateName.toLowerCase().includes(name.toLowerCase());
        }),
        ["templateName"],
        ["asc"]
      );
    } else {
      return _.orderBy(
        data.filter(({ templateName, plugin }: IMessageTemplateItem) => {
          return (
            plugin.toLowerCase() === type &&
            templateName.toLowerCase().includes(name.toLowerCase())
          );
        }),
        ["templateName"],
        ["asc"]
      );
    }
  } catch (error) {
    appInsights.trackException(`MessageTemplate FIlter Exeception - ${error}`);
    return [];
  }
};

/**
 * @description Get Message templates display names
 * @param templateVersionBMDs BMD list
 * @returns
 */
export const getMessageTemplatesDisplayNames = (
  templates: Array<IMessageTemplateItem>
): Array<string> => {
  try {
    if (Array.isArray(templates)) {
      return templates.map((template) => template.displayName);
    } else {
      return [];
    }
  } catch (error) {
    return [];
  }
};

/**
 * @description Encode unicode text
 * @param {string} text
 * @returns {string} encoded string
 */
function unicodeTextToBinary(text: string) {
  let result = "";
  try {
    const codeUnits = new Uint16Array(text.length);
    for (let i = 0; i < codeUnits.length; i++) {
      codeUnits[i] = text.charCodeAt(i);
    }
    const charCodes = new Uint8Array(codeUnits.buffer);
    for (let i = 0; i < charCodes.byteLength; i++) {
      result += String.fromCharCode(charCodes[i]);
    }
    return result;
  } catch (error) {
    appInsights.trackException(
      `MessageTemplate toBinary Exeception - ${error}`
    );
    return result;
  }
}

/**
 * @description Encode htmlContent with binary converion
 * @param {any} content content to be encoded with binary
 * @param {any} template message template
 * @returns {string} encoded string
 */
const contentEncodeWithBinary = (
  content: any,
  template: any
): Array<string> => {
  try {
    if (content.length > 0) {
      return content.map((textItem: any) =>
        btoa(unicodeTextToBinary(textItem))
      );
    } else {
      return template.content.map((textItem: any) =>
        btoa(unicodeTextToBinary(textItem))
      );
    }
  } catch {
    return [];
  }
};

/**
 * @description Encode htmlContent without  binary converion
 * @param {any} content content to be encoded with binary
 * @param {any} template message template
 * @returns {string} encoded string
 */
const contentEncodeWithoutBinary = (
  content: any,
  template: any
): Array<string> => {
  try {
    if (content.length > 0) {
      return content.map((textItem: any) => btoa(textItem));
    } else {
      return template.content.map((textItem: any) => btoa(textItem));
    }
  } catch {
    return [];
  }
};

/**
 * @description Set HTMLContent to Request
 * @param {string} pluginName -Template plugin type
 * @param {any} content content to be encoded with binary
 * @param {any} template message template
 * @returns {Array<string>} - Encoded data array
 */
export const setHTMLContent = (
  pluginName: string,
  content: any,
  template: any
): Array<string> => {
  try {
    switch (pluginName.toLowerCase()) {
      case DocumentType.PDF:
        return contentEncodeWithBinary(content, template);
      default:
        return contentEncodeWithoutBinary(content, template);
    }
  } catch (error) {
    appInsights.trackException(
      `MessageTemplate setHTMLContent Exeception - ${error}`
    );
    return contentEncodeWithoutBinary(content, template);
  }
};

/**
 * @description Get receiver email address for v1
 * @param {any} domainMessageBasicData - domain message basic data
 * @returns {string} - Receiver email address
 */
export const getReceiverEmailForV1 = (domainMessageBasicData: any): string => {
  try {
    return (
      domainMessageBasicData.entityDetails.find(
        (entity: any) => entity.receiver === ReceiverType.DEBTOR
      )?.emailAddress ?? ""
    );
  } catch (error) {
    return "";
  }
};

/**
 * @description Get receiver email address for v2
 * @param {any} domainMessageBasicData - domain message basic data
 * @param {any} data message template data
 * @returns {string} - Receiver email address
 */
export const getReceiverEmailForV2 = (
  data: any,
  domainMessageBasicData: any
): string => {
  try {
    if (data?.settings?.Emailreciever) {
      return data.settings.Emailreciever.trim();
    } else if (domainMessageBasicData && domainMessageBasicData.entityDetails) {
      const receiver = domainMessageBasicData.entityDetails.find(
        (entity: any) => entity.receiver === ReceiverType.DEBTOR
      );
      return receiver?.emailAddress?.trim() ?? "";
    }
    return "";
  } catch (error) {
    return "";
  }
};

/**
 * @description Add contenteditable attribute for html tag
 * @param htmlStr html string
 * @returns
 */
const addEditableAttribute = (htmlStr: string): string => {
  try {
    return htmlStr.replace("html", 'html contenteditable="true"');
  } catch (error) {
    return "";
  }
};

/**
 * @description Set html string to iframe
 * @param html -{string} html string
 */
export const setHTMLCode = (html: string) => {
  try {
    const editorId = document.getElementById("editor") as any;
    editorId.srcdoc = addEditableAttribute(html);
    editorId.src = window.location.origin;
  } catch (error) {}
};

/**
 * @description Get all template content with editable pages
 * @param {Array<any>} editTemplateContent - domain message basic data
 * @param {number} currentPage current page
 * @returns Template
 */
export const getFullContent = (
  editTemplateContent: Array<any>,
  currentPage: number
): Array<string> => {
  try {
    const editorId = document.getElementById("editor") as any;
    const innerHTML = editorId.contentWindow.document.documentElement.outerHTML;
    const fullContent = editTemplateContent;
    fullContent[currentPage] = innerHTML;
    return fullContent;
  } catch (error) {
    return editTemplateContent;
  }
};

/**
 * Get entity type for active domain view tab
 * @param entityType current tab entity type
 * @returns entity type for message template payload
 */
export const getEntityTypeForPayload = (
  entityType: string | undefined
): string => {
  try {
    switch (entityType) {
      case EntityType.CASE:
        return LevelType.CASE;
      case EntityType.SUBCASE:
        return LevelType.SUBCASE;
      case EntityType.AR:
        return LevelType.AR;
      default:
        return "";
    }
  } catch (error) {
    return "";
  }
};
